import { getRegion, payoutMonthFactors } from "../data/policy";
import type {
  ContributionSegment,
  OriginalRetirementAgeType,
  PensionInput,
  PensionResult,
  RegionPolicy,
  ScenarioAssumption,
  UserProfile
} from "../types";
import { addMonths, diffMonths, enumerateMonths, formatMonth, getYear, parseMonth } from "./date";

const reformStartMonth = parseMonth("2025-01");

function originalRetirementAge(type: OriginalRetirementAgeType): number {
  if (type === "female50") return 50;
  if (type === "female55") return 55;
  return 60;
}

function delayedRetirementCap(type: OriginalRetirementAgeType): number {
  if (type === "female50") return 60;
  return 36;
}

function delayStepMonths(type: OriginalRetirementAgeType): number {
  return type === "female50" ? 2 : 4;
}

export function calculateLegalRetirementMonth(profile: UserProfile): string {
  const baseAge = originalRetirementAge(profile.originalRetirementAgeType);
  const baseRetirement = parseMonth(addMonths(profile.birthMonth, baseAge * 12));
  if (baseRetirement < reformStartMonth) return formatMonth(baseRetirement);

  const elapsed = baseRetirement - reformStartMonth;
  const delayMonths = Math.min(
    delayedRetirementCap(profile.originalRetirementAgeType),
    Math.floor(elapsed / delayStepMonths(profile.originalRetirementAgeType)) + 1
  );

  return formatMonth(baseRetirement + delayMonths);
}

export function requiredContributionMonths(retirementYear: number): number {
  if (retirementYear < 2030) return 15 * 12;
  return Math.min(20 * 12, 15 * 12 + (retirementYear - 2029) * 6);
}

function clampContributionBase(base: number, region: RegionPolicy): number {
  if (base <= 0) return 0;
  return Math.min(Math.max(base, region.baseMin), region.baseMax);
}

function monthsByRegion(segments: ContributionSegment[], deemedMonths: number): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const segment of segments) {
    if (segment.paymentStatus !== "paid" && segment.paymentStatus !== "retroPaid") continue;
    const months = enumerateMonths(segment.startMonth, segment.endMonth).length;
    totals[segment.regionCode] = (totals[segment.regionCode] ?? 0) + months;
  }

  if (deemedMonths > 0 && segments[0]) {
    totals[segments[0].regionCode] = (totals[segments[0].regionCode] ?? 0) + deemedMonths;
  }

  return totals;
}

export function determineBenefitRegion(profile: UserProfile, segments: ContributionSegment[]): string {
  const totals = monthsByRegion(segments, profile.deemedContributionMonths);

  if (profile.currentInsuranceRegionCode === profile.hukouRegionCode) {
    return profile.hukouRegionCode;
  }

  if ((totals[profile.currentInsuranceRegionCode] ?? 0) >= 120) {
    return profile.currentInsuranceRegionCode;
  }

  const orderedRegions = [...segments]
    .filter((segment) => segment.paymentStatus === "paid" || segment.paymentStatus === "retroPaid")
    .sort((a, b) => parseMonth(a.endMonth) - parseMonth(b.endMonth))
    .map((segment) => segment.regionCode);

  for (let index = orderedRegions.length - 1; index >= 0; index -= 1) {
    const regionCode = orderedRegions[index];
    if ((totals[regionCode] ?? 0) >= 120) return regionCode;
  }

  return profile.hukouRegionCode;
}

function contributionBaseForStrategy(
  salary: number,
  futureRegion: RegionPolicy,
  assumption: ScenarioAssumption
): number {
  if (assumption.baseStrategy === "p60") return futureRegion.averageWage * 0.6;
  if (assumption.baseStrategy === "p100") return futureRegion.averageWage;
  if (assumption.baseStrategy === "p300") return futureRegion.averageWage * 3;
  return salary;
}

function projectedPensionBase(region: RegionPolicy, retirementMonth: string, wageGrowthRateAnnual: number): number {
  const years = Math.max(0, getYear(retirementMonth) - 2026);
  return region.pensionBase * (1 + wageGrowthRateAnnual) ** years;
}

function getPayoutFactor(retirementAgeYears: number): number {
  const age = Math.max(50, Math.min(65, Math.round(retirementAgeYears)));
  return payoutMonthFactors[age] ?? 139;
}

function currentMonth(): string {
  return "2026-06";
}

type AccountProjection = {
  balance: number;
  paidMonths: number;
  averageIndexNumerator: number;
  finalSalary: number;
};

function projectHistoricalContributions(
  segments: ContributionSegment[],
  assumption: ScenarioAssumption
): AccountProjection {
  let balance = 0;
  let paidMonths = 0;
  let averageIndexNumerator = 0;
  let finalSalary = 0;

  for (const segment of segments) {
    const region = getRegion(segment.regionCode);
    for (const month of enumerateMonths(segment.startMonth, segment.endMonth)) {
      if (segment.paymentStatus !== "paid" && segment.paymentStatus !== "retroPaid") continue;
      const base = clampContributionBase(segment.contributionBase || segment.monthlySalary, region);
      const accountCredit = base * region.accountCreditRate;
      balance = (balance + accountCredit) * (1 + assumption.accountInterestRateAnnual / 12);
      averageIndexNumerator += base / region.averageWage;
      paidMonths += 1;
      finalSalary = Math.max(finalSalary, segment.monthlySalary);
      void month;
    }
  }

  return { balance, paidMonths, averageIndexNumerator, finalSalary };
}

function projectFutureContributions(
  startMonth: string,
  retirementMonth: string,
  salarySeed: number,
  assumption: ScenarioAssumption
): AccountProjection {
  if (assumption.contributionUntil === "stopNow") {
    return { balance: 0, paidMonths: 0, averageIndexNumerator: 0, finalSalary: salarySeed };
  }

  const futureRegion = getRegion(assumption.futureRegionCode);
  let balance = 0;
  let paidMonths = 0;
  let averageIndexNumerator = 0;
  let finalSalary = salarySeed;
  const startSerial = parseMonth(startMonth);

  for (const month of enumerateMonths(startMonth, retirementMonth)) {
    const elapsedYears = Math.max(0, diffMonths(formatMonth(startSerial), month) / 12);
    const salary = salarySeed * (1 + assumption.salaryGrowthRateAnnual) ** elapsedYears;
    const base = clampContributionBase(contributionBaseForStrategy(salary, futureRegion, assumption), futureRegion);
    const accountCredit = base * futureRegion.accountCreditRate;
    balance = (balance + accountCredit) * (1 + assumption.accountInterestRateAnnual / 12);
    averageIndexNumerator += base / futureRegion.averageWage;
    paidMonths += 1;
    finalSalary = salary;
  }

  return { balance, paidMonths, averageIndexNumerator, finalSalary };
}

export function calculatePension(input: PensionInput): PensionResult {
  const { profile, segments, assumption } = input;
  const legalRetirementMonth = calculateLegalRetirementMonth(profile);
  const selectedRetirementMonth = legalRetirementMonth;
  const benefitRegionCode = determineBenefitRegion(profile, segments);
  const benefitRegion = getRegion(benefitRegionCode);

  const historical = projectHistoricalContributions(segments, assumption);
  const futureStartMonth = addMonths(currentMonth(), 1);
  const future = projectFutureContributions(
    futureStartMonth,
    legalRetirementMonth,
    historical.finalSalary || benefitRegion.averageWage,
    assumption
  );

  const actualContributionMonths =
    historical.paidMonths + future.paidMonths + Math.max(0, profile.deemedContributionMonths);
  const contributionYears = actualContributionMonths / 12;
  const minimumContributionMonthsRequired = requiredContributionMonths(getYear(selectedRetirementMonth));
  const averageContributionIndex =
    historical.paidMonths + future.paidMonths > 0
      ? (historical.averageIndexNumerator + future.averageIndexNumerator) /
        (historical.paidMonths + future.paidMonths)
      : 0;

  const grownKnownBalance =
    profile.knownAccountBalance > 0
      ? profile.knownAccountBalance *
        (1 + assumption.accountInterestRateAnnual / 12) **
          Math.max(0, diffMonths(profile.knownAccountBalanceMonth, selectedRetirementMonth))
      : 0;
  const accountBalanceAtRetirement =
    (grownKnownBalance || historical.balance) + future.balance;

  const pensionBase = projectedPensionBase(
    benefitRegion,
    selectedRetirementMonth,
    assumption.wageGrowthRateAnnual
  );
  const indexedAverageWage = pensionBase * averageContributionIndex;
  const basicPension = ((pensionBase + indexedAverageWage) / 2) * contributionYears * 0.01;

  const retirementAgeYears = diffMonths(profile.birthMonth, selectedRetirementMonth) / 12;
  const personalAccountPension = accountBalanceAtRetirement / getPayoutFactor(retirementAgeYears);
  const transitionalPension = 0;
  const monthlyPension = basicPension + personalAccountPension + transitionalPension;
  const replacementRate = future.finalSalary > 0 ? monthlyPension / future.finalSalary : 0;
  const regionContributionMonths = monthsByRegion(segments, profile.deemedContributionMonths);
  const warnings: string[] = [];

  if (actualContributionMonths < minimumContributionMonthsRequired) {
    warnings.push("累计缴费年限不足，暂不满足按月领取基本养老金条件。");
  }
  if (profile.deemedContributionMonths > 0) {
    warnings.push("已计入视同缴费月数，但过渡性养老金需按地方规则进一步核验。");
  }
  if (assumption.contributionUntil === "stopNow") {
    warnings.push("停缴情景不会新增未来缴费月数，只滚动既有个人账户余额。");
  }

  return {
    legalRetirementMonth,
    selectedRetirementMonth,
    benefitRegionCode,
    benefitRegionName: benefitRegion.name,
    minimumContributionMonthsRequired,
    actualContributionMonths,
    contributionYears,
    isEligible: actualContributionMonths >= minimumContributionMonthsRequired,
    averageContributionIndex,
    accountBalanceAtRetirement,
    monthlyPension,
    annualPension: monthlyPension * 12,
    replacementRate,
    components: {
      basicPension,
      personalAccountPension,
      transitionalPension
    },
    formulaTrace: [
      {
        label: "待遇领取地",
        value: benefitRegion.name,
        note: "按当前养老保险关系所在地、户籍地和各参保地累计缴费满 10 年规则判断。"
      },
      {
        label: "最低缴费年限",
        value: `${minimumContributionMonthsRequired} 个月`,
        note: "2030 年起逐年提高 6 个月，最高 20 年。"
      },
      {
        label: "平均缴费指数",
        value: averageContributionIndex.toFixed(3),
        note: "按月度缴费基数相对当地平均工资估算。"
      },
      {
        label: "基础养老金",
        value: `(${pensionBase.toFixed(0)} + ${indexedAverageWage.toFixed(0)}) / 2 * ${contributionYears.toFixed(2)} * 1%`,
        note: "使用待遇领取地计发基数和本人指数化平均工资。"
      },
      {
        label: "个人账户养老金",
        value: `${accountBalanceAtRetirement.toFixed(0)} / ${getPayoutFactor(retirementAgeYears)}`,
        note: "个人账户储存额按退休年龄对应计发月数折算。"
      }
    ],
    warnings,
    regionContributionMonths
  };
}
